import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { maturationService } from '../../services/maturationService';
import WelcomeSection from './components/WelcomeSection';
import ConsentSection from './components/ConsentSection';
import ChildrenList from './components/ChildrenList';
import ChildProfileForm from './components/ChildProfileForm';
import MeasurementEntry from './components/MeasurementEntry';
import GrowthDashboard from './components/GrowthDashboard';
import InsightsScreen from './components/InsightsScreen';
import SettingsScreen from './components/SettingsScreen';
import MeasurementScheduleScreen from './components/MeasurementScheduleScreen';
import ContactButton from '../../components/ContactButton';

const MaturationMonitor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guardianProfile, setGuardianProfile] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [currentView, setCurrentView] = useState('welcome');
  const [error, setError] = useState(null);
  const heightChartRef = useRef(null);
  const velocityChartRef = useRef(null);

  useEffect(() => {
    loadGuardianProfile();

    // Listen for navigation events from settings
    const handleNavigateToSchedule = () => {
      setCurrentView('measurement_schedule');
    };

    window.addEventListener('navigate-to-schedule', handleNavigateToSchedule);

    return () => {
      window.removeEventListener('navigate-to-schedule', handleNavigateToSchedule);
    };
  }, []);

  const loadGuardianProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const profile = await maturationService?.getGuardianProfile();
      setGuardianProfile(profile);

      if (profile?.consentStatus === 'accepted') {
        const childrenData = await maturationService?.getChildren();
        setChildren(childrenData);
        if (childrenData?.length > 0) {
          setCurrentView('children_list');
        } else {
          setCurrentView('create_child');
        }
      } else {
        setCurrentView('welcome');
      }
    } catch (err) {
      console.error('Error loading guardian profile:', err);
      setError(err?.message || 'Failed to load profile');
      setCurrentView('welcome');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentAccepted = async () => {
    try {
      setError(null);
      if (guardianProfile) {
        await maturationService?.updateGuardianProfile({
          consentStatus: 'accepted',
          consentAcceptedAt: new Date()?.toISOString()
        });
      } else {
        await maturationService?.createGuardianProfile(true);
      }
      await loadGuardianProfile();
      setCurrentView('create_child');
    } catch (err) {
      console.error('Error accepting consent:', err);
      setError(err?.message || 'Failed to save consent');
    }
  };

  const handleChildCreated = async (newChild) => {
    try {
      setError(null);
      const created = await maturationService?.createChild(newChild);
      setChildren([...children, created]);
      setSelectedChild(created);
      setCurrentView('measurement_entry');
    } catch (err) {
      console.error('Error creating child:', err);
      setError(err?.message || 'Failed to create child profile');
    }
  };

  const handleChildSelected = (child) => {
    setSelectedChild(child);
    setCurrentView('dashboard');
  };

  const handleMeasurementAdded = () => {
    setCurrentView('dashboard');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-error/10 border border-error rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Icon name="AlertCircle" className="w-5 h-5 text-error mr-3 mt-0.5" />
            <div>
              <p className="text-error font-medium">Error</p>
              <p className="text-error/80 text-sm">{error}</p>
            </div>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'welcome':
        return <WelcomeSection onContinue={() => setCurrentView('consent')} />;
      
      case 'consent':
        return <ConsentSection onAccept={handleConsentAccepted} onDecline={() => navigate('/dashboard-overview')} />;
      
      case 'create_child':
        return <ChildProfileForm onSubmit={handleChildCreated} onCancel={() => setCurrentView('children_list')} />;
      
      case 'children_list':
        return (
          <ChildrenList 
            children={children} 
            onSelectChild={handleChildSelected}
            onAddChild={() => setCurrentView('create_child')}
            onSettings={() => setCurrentView('settings')}
          />
        );
      
      case 'measurement_entry':
        return (
          <MeasurementEntry 
            child={selectedChild}
            onComplete={handleMeasurementAdded}
            onCancel={() => setCurrentView('dashboard')}
          />
        );
      
      case 'dashboard':
        return (
          <GrowthDashboard 
            child={selectedChild}
            onAddMeasurement={() => setCurrentView('measurement_entry')}
            onViewInsights={(heightRef, velocityRef) => {
              heightChartRef.current = heightRef?.current;
              velocityChartRef.current = velocityRef?.current;
              setCurrentView('insights');
            }}
            onBack={() => setCurrentView('children_list')}
          />
        );
      
      case 'insights':
        return (
          <InsightsScreen 
            child={selectedChild}
            onBack={() => setCurrentView('dashboard')}
            heightChartRef={heightChartRef}
            velocityChartRef={velocityChartRef}
          />
        );
      
      case 'settings':
        return (
          <SettingsScreen 
            guardianProfile={guardianProfile}
            children={children}
            onBack={() => setCurrentView('children_list')}
            onChildDeleted={loadGuardianProfile}
            onNavigateToSchedule={() => setCurrentView('measurement_schedule')}
          />
        );
      
      case 'measurement_schedule':
        return (
          <MeasurementScheduleScreen 
            guardianProfile={guardianProfile}
            onBack={() => setCurrentView('settings')}
          />
        );
      
      default:
        return <WelcomeSection onContinue={() => setCurrentView('consent')} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 pb-24">
        <div className="p-6 max-w-6xl mx-auto">
          {renderContent()}
        </div>
      </main>
      <ContactButton />
    </div>
  );
};

export default MaturationMonitor;